import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { UserInfoService } from 'src/app/shared/services/user-info.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { CockpitManualService } from 'src/app/shared/services/cockpit-manual.service';
import { UserAccount } from 'src/models/user.types';
import { MenuUserProfileComponent } from 'src/app/cockpit/main-menu/menu-user-profile/menu-user-profile.component';

/**
 * Top-bar user controls: manual and the user-account dropdown
 * (settings, admin, theme, logout). Replaces the old left-sliding account panel.
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-menu-user-top',
  templateUrl: './menu-user-top.html',
  styleUrls: ['./menu-user-top.scss'],
})
export class MenuUserTopComponent implements OnInit, OnDestroy {
  userAccountInfo: UserAccount;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    public dialog: MatDialog,
    public routing: RoutingService,
    public config: ConfigService,
    public userInfo: UserInfoService,
    public featureFlagsService: FeatureFlagsService,
    public snackBar: MatSnackBar,
    private manual: CockpitManualService,
  ) {}

  ngOnInit(): void {
    this.featureFlagsService.accountInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((accountInfo) => {
        this.userAccountInfo = accountInfo;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Full name from account info, falling back to the email. */
  get displayName(): string {
    const first = this.userAccountInfo?.first_name?.trim();
    const last = this.userAccountInfo?.last_name?.trim();
    const full = [first, last].filter(Boolean).join(' ');
    return full || '';
  }

  /** Toggle dark/light via the theme query-param (RoutingService applies it). */
  toggleTheme(dark: boolean): void {
    this.router.navigate([], {
      queryParams: { theme: dark ? 'dark' : 'light' },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Map the current route path to an anchor in the Cockpit guide.
   * Returns an empty string when no specific section applies.
   */
  private currentGuideAnchor(): string {
    const path = window.location.pathname;
    if (path.includes('/widget')) return '';
    if (path.includes('/audit')) return 'news-view';
    if (path.includes('/portfolio/scanner')) return 'scan';
    if (path.includes('/benchmark')) return 'benchmark';
    if (path.includes('/chart')) return 'charts-view';
    if (path.includes('/query')) return 'collections';
    if (path.includes('/search')) return 'charts-view';
    if (path.includes('/invest/signal')) return 'signals';
    if (path.includes('/invest/trending')) return 'trending';
    if (path.includes('/chat')) return 'assistant';
    if (path.includes('/newsletter')) return 'newsletter';
    return '';
  }

  get showGuide(): boolean {
    return this.currentGuideAnchor() !== '';
  }

  /**
   * Open the Cockpit user manual in a dedicated tab, jumping to the section
   * that matches the current route when possible.
   */
  openManual(): void {
    this.manual.open(this.currentGuideAnchor());
  }

  openUserSettingsPopup(): void {
    this.dialog.open(MenuUserProfileComponent, {
      disableClose: false,
      closeOnNavigation: true,
      data: {
        userInfo: this.userAccountInfo,
      },
    });
  }

  onUserAdministration(): void {
    const portalUrl =
      this.config.appConfig.urlCustomerPortal ||
      this.config.appConfig.urlCustomerApi;
    const domainCurrent = this.getDomain(window.location.href);
    const domainCustomerApi = this.getDomain(portalUrl);

    // When cockpit and the portal share a registrable domain, the HttpOnly
    // session cookie set by the customer API at login is already sent to the
    // portal and authorizes it — no client-side cookie write is needed. On an
    // unrelated domain (on-prem) that cookie can't span origins, so fall back to
    // the portal's own login.
    if (domainCurrent === domainCustomerApi) {
      window.open(portalUrl + '/v1');
    } else {
      window.open(portalUrl + '/v1/login');
    }
  }

  getDomain(url): string {
    // Base guards against relative inputs so it never throws; absolute URLs
    // ignore the base, preserving behavior for real customer URLs.
    const hostName = new URL(url, window.location.origin).hostname;
    let domain = hostName;
    if (hostName != null) {
      const parts = hostName.split('.').reverse();
      if (parts != null && parts.length > 1) {
        domain = parts[1] + '.' + parts[0];
      }
    }
    return domain;
  }

  getToken(): void {
    if (this.config.appConfig.setup.useStorage) {
      const token = localStorage.getItem('token');
      this.copyMessage(token);
      this.snackBar.open('Token copied to clipboard.', 'OK', {
        duration: 5000,
      });
    }
  }

  copyMessage(val): void {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }
}
